// localStorage key where the tenant domain is stored after login / signup
export const TENANT_DOMAIN_KEY = 'tenant_domain';

const TENANT_ID_KEY = 'tenant_id';
const IS_PRODUCTION = process.env.REACT_APP_ENV === 'production';
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const PEER_REFRESH_WAIT_MS = 5000;
const INIT_REFRESH_MAX_ATTEMPTS = 3;
const INIT_REFRESH_BACKOFF_MS = [250, 500];
const AUTH_LOCK_NAME = 'mms-auth-refresh';

// ---------------------------------------------------------------------------
// In-memory access token store
// ---------------------------------------------------------------------------
let _accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let pageIsUnloading = false;
let peerRefreshInProgress = false;

export type RefreshFailureReason = 'auth' | 'transient' | 'unavailable';

export class RefreshTokenError extends Error {
  readonly reason: RefreshFailureReason;
  readonly statusCode?: number;

  constructor(reason: RefreshFailureReason, message?: string, statusCode?: number) {
    super(message ?? 'Token refresh failed');
    this.name = 'RefreshTokenError';
    this.reason = reason;
    this.statusCode = statusCode;
  }
}

type AuthChannelMessage =
  | { type: 'refresh-start' }
  | { type: 'refresh-done'; access: string }
  | { type: 'refresh-failed' };

const AUTH_CHANNEL =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('mms-auth') : null;

let peerRefreshResolvers: Array<(token: string | null) => void> = [];

function resolvePeerWaiters(token: string | null): void {
  peerRefreshResolvers.forEach((resolve) => resolve(token));
  peerRefreshResolvers = [];
}

if (AUTH_CHANNEL) {
  AUTH_CHANNEL.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
    const msg = event.data;
    if (msg?.type === 'refresh-start') {
      peerRefreshInProgress = true;
    }
    if (msg?.type === 'refresh-done' && msg.access) {
      peerRefreshInProgress = false;
      _accessToken = msg.access;
      scheduleProactiveRefresh(msg.access);
      resolvePeerWaiters(msg.access);
    }
    if (msg?.type === 'refresh-failed') {
      peerRefreshInProgress = false;
      resolvePeerWaiters(null);
    }
  };
}

if (typeof document !== 'undefined') {
  const markUnloading = () => {
    pageIsUnloading = true;
  };
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('beforeunload', markUnloading);
}

function canStartProactiveRefresh(): boolean {
  return !pageIsUnloading && document.visibilityState === 'visible';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientRefreshStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

function decodeJwtExpMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized)) as { exp?: number };
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function scheduleProactiveRefresh(token: string | null): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
  if (!token) return;

  const expMs = decodeJwtExpMs(token);
  if (!expMs) return;

  const refreshAt = expMs - REFRESH_BUFFER_MS;
  const delay = refreshAt - Date.now();
  if (delay <= 0) {
    if (canStartProactiveRefresh()) {
      void getRefreshedAccessToken().catch(() => {});
    }
    return;
  }

  proactiveRefreshTimer = setTimeout(() => {
    if (!canStartProactiveRefresh()) return;
    void getRefreshedAccessToken().catch(() => {});
  }, delay);
}

export function setAccessToken(token: string | null): void {
  _accessToken = token;
  scheduleProactiveRefresh(token);
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function clearAuthStorage(): void {
  _accessToken = null;
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
  localStorage.removeItem('auth_user');
  localStorage.removeItem('logged_in');
  localStorage.removeItem(TENANT_DOMAIN_KEY);
  localStorage.removeItem(TENANT_ID_KEY);
}

/**
 * Attempt a silent token refresh on app startup.
 * Returns the new access token on success, or null if the session has expired.
 * Retries transient failures; only returns null on definitive auth failure (401).
 */
export async function initAccessToken(): Promise<string | null> {
  for (let attempt = 0; attempt < INIT_REFRESH_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await refreshAccessToken({ redirectOnFailure: false });
    } catch (err) {
      const reason = err instanceof RefreshTokenError ? err.reason : 'transient';
      if (reason === 'auth') {
        return null;
      }
      if (attempt < INIT_REFRESH_MAX_ATTEMPTS - 1) {
        await sleep(INIT_REFRESH_BACKOFF_MS[attempt] ?? 500);
        continue;
      }
      return null;
    }
  }
  return null;
}

export type InitAccessTokenResult =
  | { status: 'ok'; token: string }
  | { status: 'auth_failed' }
  | { status: 'transient_failed' };

/**
 * Bootstrap helper with explicit failure classification for App.tsx.
 */
export async function bootstrapAccessToken(): Promise<InitAccessTokenResult> {
  for (let attempt = 0; attempt < INIT_REFRESH_MAX_ATTEMPTS; attempt += 1) {
    try {
      const token = await refreshAccessToken({ redirectOnFailure: false });
      return { status: 'ok', token };
    } catch (err) {
      const reason = err instanceof RefreshTokenError ? err.reason : 'transient';
      if (reason === 'auth') {
        return { status: 'auth_failed' };
      }
      if (attempt < INIT_REFRESH_MAX_ATTEMPTS - 1) {
        await sleep(INIT_REFRESH_BACKOFF_MS[attempt] ?? 500);
        continue;
      }
      return { status: 'transient_failed' };
    }
  }
  return { status: 'transient_failed' };
}

export function getApiBase(): string {
  const envFallback = process.env.REACT_APP_API_URL;
  const domain = localStorage.getItem(TENANT_DOMAIN_KEY);

  if (IS_PRODUCTION) {
    if (domain) return `${window.location.protocol}//${domain}/api/v1`;
    return envFallback || `${window.location.origin}/api/v1`;
  }

  if (domain) return `http://${domain}:8000/api/v1`;
  return envFallback || 'http://localhost:8000/api/v1';
}

/** Auth cookie endpoints always use the current page origin in production. */
export function getRefreshApiBase(): string {
  if (IS_PRODUCTION) {
    return `${window.location.origin}/api/v1`;
  }
  return getApiBase();
}

function getAuthHeader(): Record<string, string> {
  return _accessToken ? { Authorization: `Bearer ${_accessToken}` } : {};
}

function redirectToLogin(): void {
  clearAuthStorage();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function waitForPeerRefresh(): Promise<string | null> {
  if (!peerRefreshInProgress) {
    return null;
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      peerRefreshResolvers = peerRefreshResolvers.filter((fn) => fn !== wrappedResolve);
      resolve(null);
    }, PEER_REFRESH_WAIT_MS);

    const wrappedResolve = (token: string | null) => {
      clearTimeout(timer);
      resolve(token);
    };
    peerRefreshResolvers.push(wrappedResolve);
  });
}

async function performRefreshFetch(): Promise<string> {
  const refreshUrl = `${getRefreshApiBase()}/auth/refresh/`;
  let refreshRes: Response;
  try {
    refreshRes = await fetch(refreshUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (err) {
    console.error('Token refresh network error:', err);
    AUTH_CHANNEL?.postMessage({ type: 'refresh-failed' });
    throw new RefreshTokenError('transient', 'Token refresh network error');
  }

  if (!refreshRes.ok) {
    const statusCode = refreshRes.status;
    console.error('Token refresh failed:', statusCode);
    AUTH_CHANNEL?.postMessage({ type: 'refresh-failed' });
    if (statusCode === 401 || statusCode === 403) {
      throw new RefreshTokenError('auth', 'Session expired', statusCode);
    }
    if (isTransientRefreshStatus(statusCode)) {
      throw new RefreshTokenError('transient', 'Token refresh temporarily unavailable', statusCode);
    }
    throw new RefreshTokenError('unavailable', 'Token refresh failed', statusCode);
  }

  const data = await refreshRes.json();
  setAccessToken(data.access);
  AUTH_CHANNEL?.postMessage({ type: 'refresh-done', access: data.access });
  return data.access;
}

async function runRefreshWithLock(): Promise<string> {
  const runFetch = () => performRefreshFetch();

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(AUTH_LOCK_NAME, runFetch);
  }

  return runFetch();
}

async function refreshAccessToken(options?: { redirectOnFailure?: boolean }): Promise<string> {
  const redirectOnFailure = options?.redirectOnFailure !== false;

  if (pageIsUnloading) {
    throw new RefreshTokenError('unavailable', 'Refresh skipped during page unload');
  }

  AUTH_CHANNEL?.postMessage({ type: 'refresh-start' });
  const peerToken = await waitForPeerRefresh();
  if (peerToken) {
    return peerToken;
  }

  try {
    return await runRefreshWithLock();
  } catch (err) {
    if (redirectOnFailure && err instanceof RefreshTokenError && err.reason === 'auth') {
      redirectToLogin();
    }
    throw err;
  }
}

export function getRefreshedAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function handleTokenExpiry(response: Response): Promise<Response> {
  if (response.status === 401) {
    await getRefreshedAccessToken();
  }
  return response;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!canStartProactiveRefresh() || !_accessToken) return;
    const expMs = decodeJwtExpMs(_accessToken);
    if (expMs && expMs - Date.now() < REFRESH_BUFFER_MS) {
      void getRefreshedAccessToken().catch(() => {});
    }
  });
}

export async function apiGet<T = any>(path: string): Promise<T> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    const newHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    };
    res = await fetch(url, {
      method: 'GET',
      headers: newHeaders,
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`GET ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  return res.json();
}

export async function apiPost<T = any>(path: string, body: any): Promise<T> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    const newHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    };
    res = await fetch(url, {
      method: 'POST',
      headers: newHeaders,
      body: JSON.stringify(body),
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`POST ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  return res.json();
}

export async function apiPatch<T = any>(path: string, body: any): Promise<T> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    const newHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    };
    res = await fetch(url, {
      method: 'PATCH',
      headers: newHeaders,
      body: JSON.stringify(body),
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`PATCH ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  return res.json();
}

export async function apiDelete<T = any>(path: string): Promise<T | null> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    const newHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    };
    res = await fetch(url, {
      method: 'DELETE',
      headers: newHeaders,
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`DELETE ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export async function apiPut<T = any>(path: string, body: any): Promise<T> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    const newHeaders = {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    };
    res = await fetch(url, {
      method: 'PUT',
      headers: newHeaders,
      body: JSON.stringify(body),
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`PUT ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  return res.json();
}

function navigatePopupWindow(targetWindow: Window | null | undefined, url: string): boolean {
  if (!targetWindow || targetWindow.closed) {
    return false;
  }
  try {
    targetWindow.location.href = url;
    targetWindow.opener = null;
    return true;
  } catch {
    try {
      targetWindow.close();
    } catch {
      // ignore close failures for cross-origin windows
    }
    return false;
  }
}

function openUrlInNewTab(url: string): boolean {
  const opened = window.open(url, '_blank');
  return opened != null;
}

async function parseBinaryResponseBody(response: Response): Promise<any> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function apiOpenPdf(path: string, targetWindow?: Window | null): Promise<void> {
  const url = `${getApiBase()}${path}`;
  let res = await fetch(url, {
    method: 'GET',
    headers: { ...getAuthHeader() },
  });

  if (res.status === 401) {
    await getRefreshedAccessToken();
    res = await fetch(url, {
      method: 'GET',
      headers: { ...getAuthHeader() },
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await parseBinaryResponseBody(res); } catch { /* ignore */ }
    const apiError: any = new Error(`GET ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }

  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/pdf')) {
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const opened = navigatePopupWindow(targetWindow, blobUrl) || openUrlInNewTab(blobUrl);
    if (!opened) {
      throw new Error('Browser blocked opening PDF. Please allow popups for this site and try again.');
    }
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  const responseBody = await parseBinaryResponseBody(res);
  const pdfUrl = (responseBody as { pdf_url?: string } | null)?.pdf_url;
  if (pdfUrl) {
    if (targetWindow && !targetWindow.closed && navigatePopupWindow(targetWindow, pdfUrl)) {
      return;
    }
    const opened = openUrlInNewTab(pdfUrl);
    if (!opened) {
      throw new Error('Browser blocked opening PDF. Please allow popups for this site and try again.');
    }
    return;
  }
  throw new Error('PDF URL missing from response');
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function isPaginatedResponse<T>(resp: unknown): resp is PaginatedResponse<T> {
  return (
    typeof resp === 'object'
    && resp !== null
    && Array.isArray((resp as PaginatedResponse<T>).results)
  );
}

export function unwrapList<T>(resp: unknown): T[] {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (isPaginatedResponse<T>(resp)) return resp.results;
  return [];
}

export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<unknown>,
  maxPages = 100,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const resp = await fetchPage(page);
    if (Array.isArray(resp)) {
      return resp as T[];
    }
    if (!isPaginatedResponse<T>(resp)) {
      return all;
    }
    all.push(...resp.results);
    if (!resp.next) break;
  }
  return all;
}

export async function apiFormPost<T = any>(path: string, formData: FormData): Promise<T> {
  const url = `${getApiBase()}${path}`;
  const headers: HeadersInit = {
    ...getAuthHeader(),
  };
  let res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (res.status === 401) {
    res = await handleTokenExpiry(res);
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { ...getAuthHeader() },
      body: formData,
    });
  }

  if (!res.ok) {
    let responseData: any = null;
    try { responseData = await res.json(); } catch { /* ignore */ }
    const apiError: any = new Error(`POST ${url} failed: ${res.status}`);
    apiError.responseData = responseData;
    apiError.statusCode = res.status;
    throw apiError;
  }
  return res.json();
}

const api = {
  get: apiGet,
  post: apiPost,
  formPost: apiFormPost,
  patch: apiPatch,
  put: apiPut,
  delete: apiDelete,
  openPdf: apiOpenPdf,
};

export default api;
